import { EffectsCommandMap } from 'dva';
import { ConnectState } from '@/models/connect';
import { FServiceAPI } from '@freelog/tools-lib';
import { listStateAndListMore } from '@/components/FListFooter';
import { decompile } from '@freelog/nmr_translator';
import type {
  InformalNodeManagerPageModelState,
  IRules,
  ChangeAction,
  FetchExhibitListAction,
  FetchThemeListAction,
  OnClickThemesAddBtnAction,
  OnClickThemesReplaceBtnAction,
  OnChangeThemeKeywordsAction,
  OnLoadMoreThemesAction,
  OnClick_ActiveThemeBtn_Action,
  OnClick_Themes_DeleteBtn_Action,
  RuleMatchAndResultReturn,
} from './types';
import { ruleMatchAndResult, mergeAuthInfoToList } from './helpers';

interface RuleMatchAndResultParams {
  nodeID: number;
  isRematch?: boolean;
}

export const themeEffects = {
  * fetchThemeList({ payload: { isRematch = true, isRestart } }: FetchThemeListAction, {
    call,
    select,
    put,
  }: EffectsCommandMap) {

    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    const params2: RuleMatchAndResultParams = {
      nodeID: informalNodeManagerPage.node_ID,
      isRematch: isRematch,
    };

    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);

    if (result.status === 2) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          theme_PageError: '匹配失败',
        },
      });
      return;
    }

    const params: Parameters<typeof FServiceAPI.InformalNode.testResources>[0] = {
      nodeId: informalNodeManagerPage.node_ID,
      onlineStatus: 2,
      resourceType: '主题',
      limit: 50,
      keywords: informalNodeManagerPage.theme_FilterKeywords || undefined,
    };
    const { data } = yield call(FServiceAPI.InformalNode.testResources, params);
    const params1: Parameters<typeof mergeAuthInfoToList>[0] = {
      nodeID: informalNodeManagerPage.node_ID,
      list: data.dataList,
    };

    const listWithAuth: any[] = yield call(mergeAuthInfoToList, params1);

    const { state, more } = listStateAndListMore({
      list_Length: listWithAuth.length,
      total_Length: data.totalItem,
      has_FilterCriteria: informalNodeManagerPage.theme_FilterKeywords !== '',
    });

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
        theme_List: [...listWithAuth]
          .sort((a, b) => {
            if (a.stateInfo.themeInfo.isActivatedTheme === 1 && b.stateInfo.themeInfo.isActivatedTheme !== 1) {
              return -1;
            }
            return 0;
          }),
        theme_ListState: state,
        theme_ListMore: more,
      },
    });
  },
  * onClickThemesAddBtn({}: OnClickThemesAddBtnAction, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        addThemeDrawer_Visible: true,
      },
    });
  },
  * onClickThemesReplaceBtn({}: OnClickThemesReplaceBtnAction, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: { replaceModal_Visible: true },
    });
  },
  * onChangeThemeKeywords({ payload }: OnChangeThemeKeywordsAction, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: { theme_FilterKeywords: payload.value },
    });

    yield put<FetchThemeListAction>({
      type: 'fetchThemeList',
      payload: {
        isRestart: true,
        isRematch: false,
      },
    });
  },
  * onLoadMoreThemes({}: OnLoadMoreThemesAction, { put }: EffectsCommandMap) {
    yield put<FetchExhibitListAction>({
      type: 'fetchExhibitList',
      payload: {
        isRematch: false,
        isRestart: false,
      },
    });
  },
  * onClick_ActiveThemeBtn({ payload }: OnClick_ActiveThemeBtn_Action, { select, call, put }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        theme_ActivatingThemeName: payload.testResourceName,
      },
    });

    const rules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = (informalNodeManagerPage.node_RuleInfo?.testRules || []).map((rr) => {
      return rr.ruleInfo;
    });

    let needHandleRules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = JSON.parse(JSON.stringify(rules));

    if (needHandleRules.some((n) => n.operation === 'activate_theme')) {
      needHandleRules = needHandleRules.map((nhr) => {
        if (nhr.operation === 'activate_theme') {
          return {
            ...nhr,
            exhibitName: payload.testResourceName,
            text: '',
          };
        }
        return nhr;
      });
    } else {
      needHandleRules = [
        ...needHandleRules,
        {
          operation: 'activate_theme',
          exhibitName: payload.testResourceName,
          text: '',
        },
      ];
    }

    const text: string = decompile(needHandleRules);

    const params: Parameters<typeof FServiceAPI.InformalNode.createRules>[0] = {
      nodeId: informalNodeManagerPage.node_ID,
      testRuleText: text,
    };
    const { data } = yield call(FServiceAPI.InformalNode.createRules, params);

    const params2: RuleMatchAndResultParams = {
      nodeID: informalNodeManagerPage.node_ID,
      isRematch: true,
    };

    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);
    if (result.status === 2) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          exhibit_PageError: '匹配失败',
        },
      });
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
        theme_ActivatingThemeName: '',
      },
    });

    yield put<FetchThemeListAction>({
      type: 'fetchThemeList',
      payload: {
        isRematch: true,
        isRestart: true,
      },
    });

  },
  * onClick_Themes_DeleteBtn({ payload }: OnClick_Themes_DeleteBtn_Action, { select, call, put }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    const rules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = (informalNodeManagerPage.node_RuleInfo?.testRules || []).map((rr) => {
      return rr.ruleInfo;
    });

    let needHandleRules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = JSON.parse(JSON.stringify(rules));

    needHandleRules = needHandleRules.filter((nhr) => {
      return nhr.exhibitName !== payload.testResourceName;
    });

    const text: string = decompile(needHandleRules);

    const params: Parameters<typeof FServiceAPI.InformalNode.createRules>[0] = {
      nodeId: informalNodeManagerPage.node_ID,
      testRuleText: text,
    };
    const { data } = yield call(FServiceAPI.InformalNode.createRules, params);

    const params2: RuleMatchAndResultParams = {
      nodeID: informalNodeManagerPage.node_ID,
      isRematch: true,
    };

    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);
    if (result.status === 2) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          exhibit_PageError: '匹配失败',
        },
      });
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
        theme_List: informalNodeManagerPage.theme_List.filter((el) => {
          return el.testResourceId !== payload.testResourceId;
        }),
      },
    });
  },
};
